import { fetchConfig, resetShimmer } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { DialogOpenEvent } from '@theme/dialog';
import { CartUpdateEvent } from '@theme/events';

/** @type {Record<string, string>} */
const recommendationCache = {};

/**
 * @returns {string[]}
 */
function getCartSectionIds() {
  /** @type {string[]} */
  const sectionIds = [];

  document.querySelectorAll('cart-items-component').forEach((component) => {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      sectionIds.push(component.dataset.sectionId);
    }
  });

  return sectionIds;
}

/**
 * @param {string} html
 * @returns {HTMLElement | null}
 */
function parseRecommendationContent(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  return (
    doc.querySelector('[data-cart-drawer-rec-content]') ||
    doc.querySelector('#shopify-section-dcoded-cart-drawer-recommendation [data-cart-drawer-rec-content]')
  );
}

class CartDrawerRecommendations extends HTMLElement {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {boolean} */
  #isLoading = false;

  /** @type {AbortController | null} */
  #dragAbort = null;

  /** @type {boolean} */
  #isDragging = false;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    document.addEventListener(CartUpdateEvent.eventName, this.#handleCartUpdate);

    const drawer = this.closest('cart-drawer-component');
    const dialog = drawer?.querySelector('dialog');

    if (this.dataset.mode === 'manual') {
      this.classList.remove('hidden');
      this.#enforceManualRecommendations();
      this.#bindScrollerDrag();
      return;
    }

    if (drawer) {
      drawer.addEventListener(DialogOpenEvent.eventName, this.#scheduleLoad, { passive: true });
    }

    if (dialog) {
      dialog.addEventListener('toggle', () => {
        if (dialog.open) {
          this.#scheduleLoad();
        }
      });
    }

    this.#scheduleLoad();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(CartUpdateEvent.eventName, this.#handleCartUpdate);
    this.#dragAbort?.abort();
    this.#dragAbort = null;
  }

  #scheduleLoad = () => {
    window.requestAnimationFrame(() => {
      this.#loadRecommendations();
    });
  };

  /** @param {CartUpdateEvent} event */
  #handleCartUpdate = (event) => {
    const cart = event.detail?.resource;
    const items = cart?.items;

    if (!Array.isArray(items) || items.length === 0) {
      this.classList.add('hidden');
      return;
    }

    if (this.dataset.mode === 'manual') {
      this.#enforceManualRecommendations();
      this.#syncManualCards(items);
      return;
    }

    const productId = String(items[items.length - 1]?.product_id ?? items[0]?.product_id ?? '');

    if (!productId) {
      this.classList.add('hidden');
      return;
    }

    this.classList.remove('hidden');

    if (productId === this.dataset.productId && this.dataset.recommendationsPerformed === 'true') {
      return;
    }

    this.dataset.productId = productId;
    delete this.dataset.recommendationsPerformed;
    this.#scheduleLoad();
  };

  /**
   * @param {Array<{ product_id?: number | string }>} items
   */
  #syncManualCards(items) {
    const cartProductIds = new Set(items.map((item) => String(item.product_id ?? '')));
    let visibleCount = 0;

    this.querySelectorAll('.cart-drawer-rec__card[data-product-id]').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;

      const inCart = cartProductIds.has(card.dataset.productId ?? '');
      card.hidden = inCart;

      if (!inCart) {
        visibleCount += 1;
      }
    });

    const scroller = this.querySelector('.cart-drawer-rec__scroller');

    if (scroller instanceof HTMLElement) {
      scroller.classList.toggle('cart-drawer-rec__scroller--single', visibleCount === 1);
    }

    if (visibleCount === 0) {
      this.classList.add('hidden');
    } else {
      this.classList.remove('hidden');
    }
  }

  /** @param {Event} event */
  #handleClick = async (event) => {
    if (this.#isDragging) return;

    const target = event.target;

    if (!(target instanceof HTMLElement)) return;

    const button = target.closest('.cart-drawer-rec__add[data-variant-id]');

    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const variantId = button.dataset.variantId;
    const card = button.closest('.cart-drawer-rec__card');

    if (!variantId) return;

    event.preventDefault();
    event.stopPropagation();

    const sections = getCartSectionIds();
    button.disabled = true;
    button.classList.add('loading');
    card?.classList.add('is-loading');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const fulfillmentType = button.dataset.fulfillmentType;
    if (fulfillmentType) {
      formData.append('properties[_Fulfillment Type]', fulfillmentType);
    }
    
    if (sections.length > 0) {
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('javascript', { body: formData })
      );

      const data = await response.json();

      if (!response.ok || data.status || data.errors) {
        throw new Error(
          typeof data.description === 'string'
            ? data.description
            : typeof data.message === 'string'
              ? data.message
              : 'Unable to add item.'
        );
      }

      const cartItemsComponent = this.closest('cart-items-component');
      const sourceSectionId =
        cartItemsComponent instanceof HTMLElement ? cartItemsComponent.dataset.sectionId : undefined;

      if (sourceSectionId && data.sections?.[sourceSectionId]) {
        morphSection(
          sourceSectionId,
          data.sections[sourceSectionId],
          cartItemsComponent?.dataset.drawer !== undefined ? 'hydration' : 'full'
        );
        resetShimmer(cartItemsComponent);
      }

      if (sourceSectionId) {
        document.dispatchEvent(
          new CartUpdateEvent(data, sourceSectionId, {
            itemCount: data.item_count,
            source: 'cart-drawer-recommendations',
            sections: data.sections,
          })
        );
      }
    } catch (error) {
      console.error(error);
      const message = error instanceof Error ? error.message : 'Unable to add item.';
      alert(message);
    } finally {
      button.classList.remove('loading');
      button.disabled = false;
      card?.classList.remove('is-loading');
    }
  };

  #bindScrollerDrag() {
    const scroller = this.querySelector('.cart-drawer-rec__scroller');

    if (!(scroller instanceof HTMLElement)) return;

    this.#dragAbort?.abort();
    this.#dragAbort = new AbortController();
    const { signal } = this.#dragAbort;

    let dragStartX = 0;
    let dragStartScrollLeft = 0;
    let hasDragged = false;

    const scrollParent = this.closest('scroll-hint.cart-drawer__items');

    const lockParent = () => {
      if (scrollParent instanceof HTMLElement) {
        scrollParent.dataset.recsDragging = 'true';
      }
    };

    const unlockParent = () => {
      if (scrollParent instanceof HTMLElement) {
        delete scrollParent.dataset.recsDragging;
      }
    };

    scroller.addEventListener(
      'pointerdown',
      (event) => {
        if (event.button !== 0) return;
        if (event.target instanceof HTMLElement && event.target.closest('.cart-drawer-rec__add')) {
          return;
        }

        this.#isDragging = false;
        hasDragged = false;
        dragStartX = event.clientX;
        dragStartScrollLeft = scroller.scrollLeft;
        scroller.classList.add('is-dragging');
        lockParent();
        scroller.setPointerCapture(event.pointerId);
      },
      { signal }
    );

    scroller.addEventListener(
      'pointermove',
      (event) => {
        if (!scroller.classList.contains('is-dragging')) return;

        const deltaX = event.clientX - dragStartX;

        if (Math.abs(deltaX) > 4) {
          hasDragged = true;
          this.#isDragging = true;
          event.preventDefault();
          event.stopPropagation();
        }

        scroller.scrollLeft = dragStartScrollLeft - deltaX;
      },
      { signal, passive: false }
    );

    const endDrag = (event) => {
      if (!scroller.classList.contains('is-dragging')) return;

      scroller.classList.remove('is-dragging');
      unlockParent();

      if (scroller.hasPointerCapture(event.pointerId)) {
        scroller.releasePointerCapture(event.pointerId);
      }

      window.setTimeout(() => {
        this.#isDragging = false;
      }, 0);
    };

    scroller.addEventListener('pointerup', endDrag, { signal });
    scroller.addEventListener('pointercancel', endDrag, { signal });
  }

  /**
   * @returns {Set<string>}
   */
  #getAllowedManualProductIds() {
    return new Set(
      (this.dataset.manualProductIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );
  }

  #enforceManualRecommendations() {
    const allowed = this.#getAllowedManualProductIds();

    if (!allowed.size) return;

    this.querySelectorAll('.cart-drawer-rec__card').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;

      if (!allowed.has(card.dataset.productId ?? '')) {
        card.remove();
      }
    });

    const scroller = this.querySelector('.cart-drawer-rec__scroller');

    if (!(scroller instanceof HTMLElement)) return;

    const visibleCards = scroller.querySelectorAll(
      '.cart-drawer-rec__card:not(.cart-drawer-rec__card--skeleton):not([hidden])'
    );

    scroller.classList.toggle('cart-drawer-rec__scroller--single', visibleCards.length === 1);

    if (visibleCards.length === 0) {
      this.classList.add('hidden');
    } else {
      this.classList.remove('hidden');
    }
  }

  async #loadRecommendations() {
    if (this.#isLoading || this.dataset.mode === 'manual' || this.dataset.manualProductIds) {
      return;
    }

    const { productId, sectionId, recommendationsPerformed } = this.dataset;
    const slot = this.querySelector('[data-slot="content"]');
    const primaryIntent = this.dataset.intent || 'related';
    const fallbackIntent = primaryIntent === 'related' ? 'complementary' : 'related';

    if (!productId || !sectionId || !slot) {
      this.classList.add('hidden');
      return;
    }

    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#isLoading = true;
    this.classList.remove('hidden');

    try {
      const html =
        (await this.#fetchSectionHtml(productId, sectionId, primaryIntent)) ||
        (await this.#fetchSectionHtml(productId, sectionId, fallbackIntent));

      if (!html) {
        this.#hide();
        return;
      }

      const content = parseRecommendationContent(html);

      if (!content?.innerHTML?.trim()) {
        this.#hide();
        return;
      }

      const hasCard = content.querySelector('.cart-drawer-rec__card');

      if (!hasCard) {
        this.#hide();
        return;
      }

      slot.innerHTML = content.innerHTML;
      this.dataset.recommendationsPerformed = 'true';
      this.classList.remove('hidden');
      this.#syncScrollerSingleClass(slot);
      this.#bindScrollerDrag();
    } catch (error) {
      console.error('Cart drawer recommendations:', error);
      this.#hide();
    } finally {
      this.#isLoading = false;
    }
  }

  /**
   * @param {string} productId
   * @param {string} sectionId
   * @param {string} intent
   * @returns {Promise<string | null>}
   */
  async #fetchSectionHtml(productId, sectionId, intent) {
    const baseUrl = this.dataset.url;

    if (!baseUrl) return null;

    const url = `${baseUrl}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
    const html = await this.#fetchRecommendations(url);
    const content = parseRecommendationContent(html);

    if (!content?.querySelector('.cart-drawer-rec__card')) {
      return null;
    }

    return html;
  }

  /**
   * @param {string} url
   * @returns {Promise<string>}
   */
  async #fetchRecommendations(url) {
    const cached = recommendationCache[url];

    if (cached) {
      return cached;
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    const response = await fetch(url, {
      signal: this.#activeFetch.signal,
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`Recommendations request failed (${response.status})`);
    }

    const text = await response.text();
    recommendationCache[url] = text;

    return text;
  }

  /**
   * @param {ParentNode} root
   */
  #syncScrollerSingleClass(root) {
    const scroller = root.querySelector('.cart-drawer-rec__scroller');

    if (!(scroller instanceof HTMLElement)) return;

    const visibleCards = scroller.querySelectorAll(
      '.cart-drawer-rec__card:not(.cart-drawer-rec__card--skeleton):not([hidden])'
    );

    scroller.classList.toggle('cart-drawer-rec__scroller--single', visibleCards.length === 1);
  }

  #hide() {
    this.classList.add('hidden');
    delete this.dataset.recommendationsPerformed;
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
